import {
  Ard,
  DebitOrCredit,
  UslLine,
  UseCase,
  UseCaseName,
} from "@amzn/fuse-accounting-central-datastore-client";
import { GlLineDescriptionSuffixHelper } from "../../../context/flare/constants";
import { STANDARD_USE_CASE_NAMES } from "src/components/constants/appConstants";

export const useUseCaseManagement = (
  formData: Ard | null,
  setFormData: (data: Ard) => void,
  setActiveTabId: (id: string) => void,
) => {
  const createBlankUslLine = (): UslLine => ({
    accountNumber: "",
    channel: "",
    companyCode: "",
    costCenter: "",
    debitOrCredit: undefined,
    location: "",
    productLine: "",
    project: "",
  });

  const reverseDebitCredit = (
    debitOrCredit: DebitOrCredit | undefined,
  ): DebitOrCredit | undefined => {
    if (!debitOrCredit) return undefined;
    return debitOrCredit === DebitOrCredit.DEBIT
      ? DebitOrCredit.CREDIT
      : DebitOrCredit.DEBIT;
  };

  const detectLanguageAndGetSuffix = (
    sourceSuffix: string | undefined,
    englishSuffix: string,
    portugueseSuffix: string,
  ): string => {
    // Check if source is in Portuguese
    if (
      sourceSuffix === "Venda_Liquida" ||
      sourceSuffix === "Amortizacao" ||
      sourceSuffix === "Cancelamento" ||
      sourceSuffix === "Cancelamento_Ajuste"
    ) {
      return portugueseSuffix;
    }
    return englishSuffix;
  };

  const copyUslLineWithModifications = (
    sourceLine: UslLine,
    englishSuffix: string,
    portugueseSuffix: string,
  ): UslLine => {
    const targetSuffix = detectLanguageAndGetSuffix(
      sourceLine.glLineDescriptionSuffix,
      englishSuffix,
      portugueseSuffix,
    );
    return {
      ...sourceLine,
      debitOrCredit: reverseDebitCredit(sourceLine.debitOrCredit),
      glLineDescriptionSuffix: targetSuffix,
    };
  };

  const getUslLinesFromUseCase = (
    sourceUseCaseName: UseCaseName,
    suffixMatcher: (suffix?: string) => boolean,
    englishTargetSuffix: string,
    portugueseTargetSuffix: string,
  ): UslLine[] => {
    if (!formData?.usecases) return [];

    const sourceUseCase = formData.usecases.find(
      (uc) => uc.name === sourceUseCaseName,
    );

    if (!sourceUseCase?.uslLines || sourceUseCase.uslLines.length === 0) {
      return [];
    }

    return sourceUseCase.uslLines
      .filter((line) => suffixMatcher(line.glLineDescriptionSuffix))
      .map((line) =>
        copyUslLineWithModifications(
          line,
          englishTargetSuffix,
          portugueseTargetSuffix,
        ),
      );
  };

  const getInitialUslLines = (useCaseName: UseCaseName): UslLine[] => {
    switch (useCaseName) {
      case UseCaseName.HARD_CANCEL_WITH_REFUND: {
        const saleLines = getUslLinesFromUseCase(
          UseCaseName.SALE,
          GlLineDescriptionSuffixHelper.isSale,
          "Cancelation",
          "Cancelamento",
        );
        const amortizationLines = getUslLinesFromUseCase(
          UseCaseName.AMORTIZATION,
          GlLineDescriptionSuffixHelper.isAmortization,
          "Cancelation_True_Up",
          "Cancelamento_Ajuste",
        );
        const allLines = [...saleLines, ...amortizationLines];
        return allLines.length > 0 ? allLines : [createBlankUslLine()];
      }

      case UseCaseName.HARD_CANCEL_WITHOUT_REFUND: {
        const amortizationLines = getUslLinesFromUseCase(
          UseCaseName.AMORTIZATION,
          GlLineDescriptionSuffixHelper.isAmortization,
          "Cancelation_True_Up",
          "Cancelamento_Ajuste",
        );
        return amortizationLines.length > 0
          ? amortizationLines
          : [createBlankUslLine()];
      }

      default:
        return [createBlankUslLine()];
    }
  };

  const getAvailableUseCaseNames = (): UseCaseName[] => {
    if (!formData) return [];

    const existingNames =
      formData.usecases?.map((useCase: UseCase) => useCase.name) || [];

    return STANDARD_USE_CASE_NAMES.filter(
      (name) => !existingNames.includes(name),
    );
  };

  const addNewUseCase = (selectedUseCaseName: string) => {
    if (formData && selectedUseCaseName) {
      const newUseCase: UseCase = {
        name: selectedUseCaseName as UseCaseName,
        uslLines: getInitialUslLines(selectedUseCaseName as UseCaseName),
      };

      let updatedUsecases: UseCase[];
      let newUseCaseIndex: number;

      if (!formData.usecases || formData.usecases.length === 0) {
        // No existing use cases, create new array with just the new use case
        updatedUsecases = [newUseCase];
        newUseCaseIndex = 0;
      } else {
        // Find the first index where useCase.name is "Other"
        const firstOtherIndex = formData.usecases.findIndex(
          (useCase) => useCase.name === UseCaseName.OTHER,
        );

        if (firstOtherIndex !== -1) {
          // Insert before the first "Other" use case
          updatedUsecases = [...formData.usecases];
          updatedUsecases.splice(firstOtherIndex, 0, newUseCase);
          newUseCaseIndex = firstOtherIndex;
        } else {
          // No "Other" use case found, append to the end
          updatedUsecases = [...formData.usecases, newUseCase];
          newUseCaseIndex = updatedUsecases.length - 1;
        }
      }

      setFormData({
        ...formData,
        usecases: updatedUsecases,
      });

      setActiveTabId(newUseCaseIndex.toString());
    }
  };

  const addUslLine = (useCaseIndex: number) => {
    if (formData && formData.usecases) {
      const updatedUsecases = [...formData.usecases];
      const useCase = updatedUsecases[useCaseIndex];

      if (useCase && useCase.uslLines) {
        const updatedLines = [...useCase.uslLines, createBlankUslLine()];

        updatedUsecases[useCaseIndex] = {
          ...useCase,
          uslLines: updatedLines,
        };

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });
      }
    }
  };

  const duplicateUslLine = (useCaseIndex: number, lineIndex: number) => {
    if (formData && formData.usecases) {
      const updatedUsecases = [...formData.usecases];
      const useCase = updatedUsecases[useCaseIndex];

      if (useCase && useCase.uslLines) {
        const updatedLines = [...useCase.uslLines];
        const lineToDuplicate = updatedLines[lineIndex];

        // Create a copy of the line using spread operator
        const duplicatedLine: UslLine = { ...lineToDuplicate };

        // Insert the duplicated line right after the original
        updatedLines.splice(lineIndex + 1, 0, duplicatedLine);

        updatedUsecases[useCaseIndex] = {
          ...useCase,
          uslLines: updatedLines,
        };

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });
      }
    }
  };

  const deleteUslLine = (useCaseIndex: number, lineIndex: number) => {
    if (formData && formData.usecases) {
      const updatedUsecases = [...formData.usecases];
      const useCase = updatedUsecases[useCaseIndex];

      if (useCase && useCase.uslLines && useCase.uslLines.length > 2) {
        const updatedLines = [...useCase.uslLines];
        updatedLines.splice(lineIndex, 1);

        updatedUsecases[useCaseIndex] = {
          ...useCase,
          uslLines: updatedLines,
        };

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });
      }
    }
  };

  const deleteUseCase = (useCaseIndex: number, activeTabId: string) => {
    if (formData && formData.usecases && formData.usecases.length > 0) {
      const updatedUsecases = [...formData.usecases];
      updatedUsecases.splice(useCaseIndex, 1);

      setFormData({
        ...formData,
        usecases: updatedUsecases,
      });

      if (parseInt(activeTabId) >= updatedUsecases.length) {
        setActiveTabId("0");
      }
    }
  };

  const duplicateUseCase = (useCaseIndex: number, targetName: UseCaseName) => {
    if (formData && formData.usecases) {
      const updatedUsecases = [...formData.usecases];
      const sourceUseCase = updatedUsecases[useCaseIndex];

      if (sourceUseCase) {
        const duplicated: UseCase = {
          ...sourceUseCase,
          name: targetName,
          altName: targetName === UseCaseName.OTHER ? "" : undefined,
          uslLines: sourceUseCase.uslLines?.map((line) => ({ ...line })),
        };
        updatedUsecases.splice(useCaseIndex + 1, 0, duplicated);

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });

        setActiveTabId((useCaseIndex + 1).toString());
      }
    }
  };

  const changeUseCaseName = (useCaseIndex: number, newName: UseCaseName) => {
    if (formData && formData.usecases) {
      const updatedUsecases = [...formData.usecases];
      const useCase = updatedUsecases[useCaseIndex];

      if (useCase) {
        // Update the use case with the new name
        updatedUsecases[useCaseIndex] = {
          ...useCase,
          name: newName as UseCaseName,
        };

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });
      }
    }
  };

  const moveUslLineUp = (useCaseIndex: number, lineIndex: number) => {
    if (formData && formData.usecases && lineIndex > 0) {
      const updatedUsecases = [...formData.usecases];
      const useCase = updatedUsecases[useCaseIndex];

      if (useCase && useCase.uslLines) {
        const updatedLines = [...useCase.uslLines];
        [updatedLines[lineIndex - 1], updatedLines[lineIndex]] = [
          updatedLines[lineIndex],
          updatedLines[lineIndex - 1],
        ];

        updatedUsecases[useCaseIndex] = {
          ...useCase,
          uslLines: updatedLines,
        };

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });
      }
    }
  };

  const moveUslLineDown = (useCaseIndex: number, lineIndex: number) => {
    if (formData && formData.usecases) {
      const updatedUsecases = [...formData.usecases];
      const useCase = updatedUsecases[useCaseIndex];

      if (
        useCase &&
        useCase.uslLines &&
        lineIndex < useCase.uslLines.length - 1
      ) {
        const updatedLines = [...useCase.uslLines];
        [updatedLines[lineIndex], updatedLines[lineIndex + 1]] = [
          updatedLines[lineIndex + 1],
          updatedLines[lineIndex],
        ];

        updatedUsecases[useCaseIndex] = {
          ...useCase,
          uslLines: updatedLines,
        };

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });
      }
    }
  };

  const moveUseCaseLeft = (useCaseIndex: number) => {
    if (formData && formData.usecases && useCaseIndex > 0) {
      const updatedUsecases = [...formData.usecases];

      // Swap with the previous use case
      [updatedUsecases[useCaseIndex - 1], updatedUsecases[useCaseIndex]] = [
        updatedUsecases[useCaseIndex],
        updatedUsecases[useCaseIndex - 1],
      ];

      setFormData({
        ...formData,
        usecases: updatedUsecases,
      });

      // Update active tab to follow the moved use case
      setActiveTabId((useCaseIndex - 1).toString());
    }
  };

  const moveUseCaseRight = (useCaseIndex: number) => {
    if (
      formData &&
      formData.usecases &&
      useCaseIndex < formData.usecases.length - 1
    ) {
      const updatedUsecases = [...formData.usecases];

      // Swap with the next use case
      [updatedUsecases[useCaseIndex], updatedUsecases[useCaseIndex + 1]] = [
        updatedUsecases[useCaseIndex + 1],
        updatedUsecases[useCaseIndex],
      ];

      setFormData({
        ...formData,
        usecases: updatedUsecases,
      });

      // Update active tab to follow the moved use case
      setActiveTabId((useCaseIndex + 1).toString());
    }
  };

  return {
    getAvailableUseCaseNames,
    addNewUseCase,
    addUslLine,
    duplicateUslLine,
    deleteUslLine,
    deleteUseCase,
    duplicateUseCase,
    changeUseCaseName,
    moveUslLineUp,
    moveUslLineDown,
    moveUseCaseLeft,
    moveUseCaseRight,
  };
};
